"use client";

import { useLocale } from "@i18n/react";
import { usePath } from "@packages/framework";
import type { ComponentPropsWithoutRef } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getCookieHasRefreshToken,
  setCookieHasRefreshToken,
} from "../../lib/cookies/cookieHasRefreshToken.cookie";
import type { JWTPayload } from "../../lib/types/schema/jwtSchema";
import { jwtSchema } from "../../lib/types/schema/jwtSchema";
import type { OauthRefresh } from "../../lib/types/schema/oauthRefreshSchema.schema";
import type { SessionContext } from "../sessionContext";
import { sessionContext } from "../sessionContext";
import type { SessionStatus } from "../types/enums/sessionStatusEnum.enum";
import { requestRefreshTokenDelete } from "../utils/requests/requestRefreshTokenDelete";
import { requestSessionLogout } from "../utils/requests/requestSessionLogout";
import { requestSessionRefresh } from "../utils/requests/requestSessionRefresh";

export type SessionProviderProps = Omit<
  ComponentPropsWithoutRef<typeof sessionContext.Provider>,
  "value"
> & { noAutoRefresh?: boolean };

export const SessionProvider = ({
  children,
  noAutoRefresh = false,
  ...props
}: SessionProviderProps) => {
  const hasRefreshToken = getCookieHasRefreshToken();

  const [status, setStatus] = useState<SessionStatus>("loading");
  const [token, setToken] = useState<null | string>(null);

  const locale = useLocale();
  const path = usePath();

  const login: SessionContext["login"] = ({ isNewUser, token }) => {
    setSessionURLSearchParameters({
      isAuthenticated: true,
      isNewUser,
    });
    setStatus("authenticated");
    setToken(token);
  };

  const logout = ({ callback }: { callback?: Function } = {}) =>
    requestSessionLogout({
      headers: { Authorization: `Bearer ${token}` },
    }).then(async () => {
      setCookieHasRefreshToken({ value: false });
      setStatus("unauthenticated");
      setToken(null);

      if (callback) await callback();
    });

  const refresh = ({ callback }: { callback?: Function } = {}) =>
    noAutoRefresh
      ? Promise.resolve({} as OauthRefresh)
      : requestSessionRefresh()
          .then(async (data) => {
            if (data.access_token) {
              setStatus("authenticated");
              setToken(data.access_token);

              if (callback) await callback(data);
              return data;
            }
          })
          .catch(async () => {
            await requestRefreshTokenDelete().then((data) => {
              if (data) {
                setCookieHasRefreshToken({ value: false });
                location.reload();
              }
            });
            return {} as OauthRefresh;
          });

  const resetSessionURLSearchParameters = useCallback(() => {
    // Read the live URL rather than the `search` closure: another URL writer
    // (e.g. Auth's own flow= param) may have written to the URL after this
    // render captured `search`. Building from a stale snapshot here would
    // silently drop that concurrent write.
    const URLParams = new URLSearchParams(window.location.search);

    URLParams.delete("isAuthenticated");
    URLParams.delete("isNewUser");

    window.history.replaceState({}, "", `${path}?${URLParams.toString()}`);
  }, [locale, path]);

  const setSessionURLSearchParameters = useCallback<
    SessionContext["setSessionURLSearchParameters"]
  >(
    ({ isAuthenticated, isNewUser }) => {
      const URLParams = new URLSearchParams(window.location.search);

      URLParams.set("isAuthenticated", isAuthenticated ? "true" : "false");
      URLParams.set("isNewUser", isNewUser ? "true" : "false");

      window.history.replaceState({}, "", `${path}?${URLParams.toString()}`);
    },
    [locale, path],
  );

  useEffect(() => {
    if (hasRefreshToken && !noAutoRefresh) {
      if (!token) refresh();
      else setStatus("authenticated");
    } else setStatus("unauthenticated");
  }, [hasRefreshToken, noAutoRefresh]);

  useEffect(() => {
    const sessionStorageToken = sessionStorage.getItem("hlwtkn");
    if (sessionStorageToken) {
      setStatus("authenticated");
      setToken(sessionStorageToken);
      sessionStorage.removeItem("hlwtkn");
    }
  }, []);

  const jwtPayload = useMemo(() => {
    if (!token?.length) return null;
    try {
      const decoded = decode(token);
      const typed = jwtSchema.safeParse(decoded);
      if (!typed.success) {
        console.error(typed.error);
      }
      return typed.data ?? null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [token]);

  return (
    <sessionContext.Provider
      value={{
        hasRefreshToken,
        jwtPayload,
        login,
        logout,
        refresh,
        resetSessionURLSearchParameters,
        setSessionURLSearchParameters,
        setStatus,
        setToken,
        status,
        token,
      }}
      {...props}
    >
      {children}
    </sessionContext.Provider>
  );
};

const decode = (jwt: string): JWTPayload | null => {
  // the jsonwebtoken package is a Node.js package and does not work in the browser, but this is all it does anyway
  try {
    const payloadSection = jwt.split(".")[1];
    const urlDecoded = decodeURIComponent(payloadSection);
    const base64Decoded = atob(urlDecoded);
    return JSON.parse(base64Decoded);
  } catch (e) {
    console.error(e);
    return null;
  }
};
